import type { FieldValues } from "react-hook-form";

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value) && !(value instanceof Date);

export const readFormDraft = <T extends FieldValues>(persistKey: string): Partial<T> | null => {
  try {
    const raw = localStorage.getItem(persistKey);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    return isPlainRecord(parsed) ? (parsed as Partial<T>) : null;
  } catch {
    return null;
  }
};

export const clearFormDraft = (persistKey: string) => {
  localStorage.removeItem(persistKey);
};

export const writeFormDraft = <T extends FieldValues>(persistKey: string, draft: Partial<T>) => {
  if (!Object.keys(draft).length) return;
  localStorage.setItem(persistKey, JSON.stringify(draft));
};

export const mergeFormDraft = <T>(baseValues: T, draft: Partial<T> | null | undefined): T => {
  if (!isPlainRecord(baseValues) || !isPlainRecord(draft)) {
    return (draft ?? baseValues) as T;
  }

  const result: Record<string, unknown> = { ...baseValues };

  Object.entries(draft).forEach(([key, value]) => {
    const baseValue = result[key];
    result[key] =
      isPlainRecord(baseValue) && isPlainRecord(value) ? mergeFormDraft(baseValue, value) : value;
  });

  return result as T;
};

const isPersistableLeaf = (value: unknown): boolean =>
  value === null ||
  typeof value === "string" ||
  typeof value === "number" ||
  typeof value === "boolean" ||
  value instanceof Date;

const isDateValue = (value: unknown): value is Date => value instanceof Date;

const areEquivalent = (currentValue: unknown, baselineValue: unknown): boolean => {
  if (currentValue === baselineValue) return true;

  const currentDate =
    currentValue instanceof Date ? currentValue : new Date(String(currentValue ?? ""));
  const baselineDate =
    baselineValue instanceof Date ? baselineValue : new Date(String(baselineValue ?? ""));

  if (!Number.isNaN(currentDate.getTime()) && !Number.isNaN(baselineDate.getTime())) {
    return currentDate.getTime() === baselineDate.getTime();
  }

  return false;
};

export const buildFormDraftFromBaseline = <T extends FieldValues>(
  values: T,
  baselineValues: T,
  persistedFields: string[],
  ignoredFields: string[] = [],
): Partial<T> => {
  const ignored = new Set<string>(ignoredFields);
  const draft: Partial<T> = {};

  for (const field of persistedFields) {
    if (ignored.has(field)) continue;

    const value = values[field as keyof T];
    const baselineValue = baselineValues[field as keyof T];

    if (areEquivalent(value, baselineValue)) continue;
    if (!isPersistableLeaf(value)) continue;

    draft[field as keyof T] = (isDateValue(value) ? value.toISOString() : value) as T[keyof T];
  }

  return draft;
};
